import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RiDeleteBinLine, RiDraggable } from '@remixicon/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/design-system/drawer';
import { SearchField } from '@/design-system/search-field';
import { toast } from '@/design-system/toaster';
import {
  DispatchDriver,
  dispatchDriversAPI,
  driverDisplayName,
} from '@/services/api/dispatchDrivers';
import { getListProvider, Provider } from '@/services/api/providers';
import {
  MAX_DRIVERS_PER_ZONE,
  ZoneDriver,
  ZoneDriverClient,
  ZoneDriverDuplicateError,
  ZoneDriverInput,
  zoneDriversAPI,
} from '@/services/api/zoneDrivers';
import { Zone } from '@/services/api/zones';

interface ZoneDriversDrawerProps {
  isOpen: boolean;
  /** Zone being edited. Null while the drawer is closed. */
  zone: Zone | null;
  /** Current roster (priority order) — the drawer's starting state. */
  drivers: ZoneDriver[];
  /**
   * Plain hub name (not "code · name") — powers the Dedicated tab through the
   * drivers `tagNames` filter, same as the dispatch flow.
   */
  hubName?: string;
  onClose: () => void;
  /** Fired with the saved roster from `data.items` — no refetch needed. */
  onSaved: (zoneId: string, drivers: ZoneDriver[]) => void;
}

/**
 * Manage a zone's driver roster ("mitra" in the UI). Every edit — reorder,
 * remove, add — is local
 * state; Save sends the whole list in one `PUT` and the server derives
 * `priority = index + 1`. That mirrors the API (no add/remove/reorder
 * endpoints) and means ops can shuffle freely before committing.
 */
const ZoneDriversDrawer: React.FC<ZoneDriversDrawerProps> = ({
  isOpen,
  zone,
  drivers,
  hubName,
  onClose,
  onSaved,
}) => {
  const [roster, setRoster] = useState<ZoneDriverInput[]>([]);
  const [saving, setSaving] = useState(false);

  const [candidates, setCandidates] = useState<DispatchDriver[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [driverTab, setDriverTab] = useState<'RECOMMENDED' | 'ALL'>('ALL');

  // Client options for the per-driver scope picker — the same provider list
  // that backs the Client picker on the shipments import modal (searchable,
  // first 30 per query). A driver's already-selected clients are merged into
  // the row's options so they always stay uncheckable.
  const [clientOptions, setClientOptions] = useState<Provider[]>([]);
  const [clientOptionsLoading, setClientOptionsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  /** Driver id whose client-scope editor is expanded (one at a time). */
  const [clientEditorFor, setClientEditorFor] = useState<number | null>(null);

  // Dirty tracking covers order AND client scope — `id:clientIds` per row.
  const rosterSignature = (rows: ZoneDriverInput[]) =>
    rows
      .map((m) => `${m.id}:${m.clients.map((c) => c.id).join('+')}`)
      .join(',');
  const initialSignature = useMemo(
    () =>
      rosterSignature(
        drivers.map((d) => ({
          id: d.driverId,
          code: d.driverCode,
          name: d.driverName,
          clients: d.clients,
        }))
      ),
    [drivers]
  );

  // Reset from props on open only — reopening is the way to discard edits, so
  // a roster refresh landing mid-edit must not wipe what ops just arranged.
  useEffect(() => {
    if (!isOpen) return;
    setRoster(
      drivers.map((d) => ({
        id: d.driverId,
        code: d.driverCode,
        name: d.driverName,
        clients: d.clients,
      }))
    );
    setSaving(false);
    setSearch('');
    setDebouncedSearch('');
    setClientEditorFor(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, zone?.id]);

  // Fetch client options while an editor row is open; re-query on search
  // (300ms debounce, same cadence as the driver search). Non-fatal: the
  // roster editor keeps working without it.
  useEffect(() => {
    if (!isOpen || clientEditorFor === null) return;
    let cancelled = false;
    const trimmed = clientSearch.trim();
    const fire = () => {
      setClientOptionsLoading(true);
      getListProvider({
        page: '1',
        size: '30',
        search: trimmed || undefined,
      })
        .then((res) => {
          if (!cancelled) setClientOptions(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setClientOptions([]);
        })
        .finally(() => {
          if (!cancelled) setClientOptionsLoading(false);
        });
    };
    const timer = trimmed ? setTimeout(fire, 300) : (fire(), undefined);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isOpen, clientEditorFor, clientSearch]);

  // Drivers tagged with this hub are the natural starting point. Split from the
  // reset effect so a late-resolving hub name only flips the tab.
  useEffect(() => {
    if (isOpen) setDriverTab(hubName ? 'RECOMMENDED' : 'ALL');
  }, [isOpen, hubName]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Cancellation matters here: opening the drawer flips ALL → RECOMMENDED one
  // render after the first fetch fires, and without it the slower unfiltered
  // response would overwrite the recommended list.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setCandidatesLoading(true);
    dispatchDriversAPI
      .list({
        active: true,
        search: debouncedSearch || undefined,
        size: 100,
        ...(driverTab === 'RECOMMENDED' && hubName ? { tagNames: hubName } : {}),
      })
      .then((res) => {
        if (!cancelled) setCandidates(res.data ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCandidates([]);
        toast.error('Could not load mitra', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      })
      .finally(() => {
        if (!cancelled) setCandidatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedSearch, driverTab, hubName]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const rosterIds = useMemo(() => roster.map((m) => m.id), [roster]);
  const inRoster = useMemo(() => new Set(rosterIds), [rosterIds]);
  const isDirty = rosterSignature(roster) !== initialSignature;
  const isFull = roster.length >= MAX_DRIVERS_PER_ZONE;

  const toggleClient = useCallback(
    (driverId: number, client: ZoneDriverClient) => {
      setRoster((prev) =>
        prev.map((m) => {
          if (m.id !== driverId) return m;
          const has = m.clients.some((c) => c.id === client.id);
          return {
            ...m,
            clients: has
              ? m.clients.filter((c) => c.id !== client.id)
              : [...m.clients, { id: client.id, name: client.name }],
          };
        })
      );
    },
    []
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoster((prev) => {
      const from = prev.findIndex((m) => m.id === active.id);
      const to = prev.findIndex((m) => m.id === over.id);
      if (from === -1 || to === -1) return prev;
      return arrayMove(prev, from, to);
    });
  }, []);

  const addDriver = (d: DispatchDriver) => {
    if (inRoster.has(d.id)) return;
    if (isFull) {
      toast.error('Roster full', {
        description: `A zone takes at most ${MAX_DRIVERS_PER_ZONE} mitra.`,
      });
      return;
    }
    setRoster((prev) => [
      ...prev,
      {
        id: d.id,
        code: d.code,
        name: driverDisplayName(d),
        // Default scope = the providers (clients) the driver is registered
        // under in driver-service. Ops can still widen (uncheck all = every
        // client) or narrow it before saving.
        clients: (d.driver_providers ?? []).map((p) => ({
          id: p.id,
          name: p.name,
        })),
      },
    ]);
  };

  const removeDriver = (id: number) => {
    setRoster((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSave = async () => {
    if (!zone || saving) return;
    setSaving(true);
    try {
      const saved = await zoneDriversAPI.replace(zone.id, roster);
      onSaved(zone.id, saved);
      toast.success('Mitra saved', {
        description:
          saved.length === 0
            ? `${zone.code} has no mitra assigned.`
            : `${zone.code} — ${saved.length} mitra in priority order.`,
      });
      onClose();
    } catch (err) {
      toast.error('Could not save mitra', {
        description:
          err instanceof ZoneDriverDuplicateError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DrawerContent
        side="right"
        size="lg"
        aria-label="Manage zone mitra"
        onInteractOutside={(e) => {
          if (saving) e.preventDefault();
        }}
      >
        <DrawerHeader>
          <div className="flex items-center gap-2 pr-8">
            {zone && (
              <span
                className="h-3 w-3 shrink-0 rounded-full border border-stroke-soft-200"
                style={{ backgroundColor: zone.color }}
              />
            )}
            <DrawerTitle className="truncate">
              {zone ? `${zone.code} mitra` : 'Zone mitra'}
            </DrawerTitle>
          </div>
          <DrawerDescription>
            {zone?.name
              ? `${zone.name} · priority 1 is dispatched first.`
              : 'Priority 1 is dispatched first.'}
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody className="space-y-5 p-5">
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="label-eyebrow">Assigned mitra</p>
              <span className="font-mono text-[11px] tabular-nums text-text-soft-400">
                {roster.length}/{MAX_DRIVERS_PER_ZONE}
              </span>
            </div>

            {roster.length === 0 ? (
              <div className="rounded-lg border border-dashed border-stroke-soft-200 px-3 py-5 text-center">
                <p className="text-[12.5px] font-semibold tracking-tight text-text-strong-950">
                  No mitra yet
                </p>
                <p className="mt-1 text-[11.5px] leading-relaxed text-text-sub-600">
                  Add mitra below. An empty list is valid — dispatch just falls
                  back to distance and load for this zone.
                </p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={rosterIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="divide-y divide-stroke-soft-200 overflow-hidden rounded-lg border border-stroke-soft-200">
                    {roster.map((m, i) => (
                      <SortableRosterRow
                        key={m.id}
                        driver={m}
                        priority={i + 1}
                        disabled={saving}
                        clientEditorOpen={clientEditorFor === m.id}
                        clientOptions={clientOptions}
                        clientOptionsLoading={clientOptionsLoading}
                        clientSearch={clientSearch}
                        onClientSearch={setClientSearch}
                        onToggleClientEditor={() => {
                          setClientSearch('');
                          setClientEditorFor((prev) =>
                            prev === m.id ? null : m.id
                          );
                        }}
                        onToggleClient={(client) => toggleClient(m.id, client)}
                        onRemove={() => removeDriver(m.id)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>

          <section>
            <p className="label-eyebrow mb-2">Add mitra</p>

            <div className="mb-2 flex items-center border-b border-stroke-soft-200">
              <button
                type="button"
                onClick={() => setDriverTab('RECOMMENDED')}
                disabled={saving || !hubName}
                title={!hubName ? 'Hub still loading…' : undefined}
                className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-[12px] font-semibold tracking-tight transition-colors disabled:cursor-not-allowed ${
                  driverTab === 'RECOMMENDED'
                    ? 'border-accent text-text-strong-950'
                    : 'border-transparent text-text-sub-600 hover:text-text-strong-950 disabled:text-text-disabled-300 disabled:hover:text-text-disabled-300'
                }`}
              >
                <i className="ri-sparkling-line text-[13px]" aria-hidden="true" />
                Dedicated Driver
              </button>
              <button
                type="button"
                onClick={() => setDriverTab('ALL')}
                disabled={saving}
                className={`inline-flex items-center border-b-2 px-3 py-1.5 text-[12px] font-semibold tracking-tight transition-colors ${
                  driverTab === 'ALL'
                    ? 'border-accent text-text-strong-950'
                    : 'border-transparent text-text-sub-600 hover:text-text-strong-950'
                }`}
              >
                All mitra
              </button>
            </div>

            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search by name, code, or phone…"
              size="sm"
              disabled={saving}
              debounceMs={0}
              className="mb-2"
            />

            <div className="max-h-[280px] divide-y divide-stroke-soft-200 overflow-y-auto rounded-lg border border-stroke-soft-200">
              {candidatesLoading ? (
                <p className="px-3 py-4 text-center text-[11.5px] text-text-sub-600">
                  Loading mitra…
                </p>
              ) : candidates.length === 0 ? (
                <p className="px-3 py-4 text-center text-[11.5px] text-text-sub-600">
                  {driverTab === 'RECOMMENDED'
                    ? search
                      ? 'No matches among mitra tagged for this hub.'
                      : `No mitra tagged "${hubName}" yet — check All mitra.`
                    : search
                      ? 'No matches.'
                      : 'Search to find mitra.'}
                </p>
              ) : (
                candidates.map((d) => {
                  const already = inRoster.has(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => addDriver(d)}
                      disabled={saving || already || isFull}
                      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                        already
                          ? 'cursor-default bg-bg-weak-50'
                          : isFull
                            ? 'cursor-not-allowed opacity-60'
                            : 'cursor-pointer hover:bg-bg-weak-50'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] tracking-tight text-text-strong-950">
                          {driverDisplayName(d)}
                        </span>
                        <span className="flex items-center gap-2 font-mono text-[11px] tabular-nums text-text-sub-600">
                          <span>{d.code}</span>
                          {d.phone_number && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span>{d.phone_number}</span>
                            </>
                          )}
                        </span>
                      </span>
                      {already ? (
                        <Badge status="neutral" size="sm" className="shrink-0">
                          Assigned
                        </Badge>
                      ) : (
                        <i
                          className="ri-add-line shrink-0 text-[16px] text-accent"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-text-sub-600">
              One mitra can cover several zones — assigning them here doesn't
              remove them anywhere else.
            </p>
          </section>
        </DrawerBody>

        <DrawerFooter className="items-center justify-between p-5 sm:justify-between">
          <span className="text-[11.5px] text-text-sub-600">
            {roster.length} mitra
            {isDirty && <span className="text-warning-base"> · unsaved</span>}
          </span>
          <div className="flex items-center gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !isDirty}
              loading={saving}
            >
              {saving ? 'Saving…' : 'Save mitra'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

interface SortableRosterRowProps {
  driver: ZoneDriverInput;
  priority: number;
  disabled: boolean;
  /** True while this row's client-scope editor is expanded. */
  clientEditorOpen: boolean;
  clientOptions: Provider[];
  clientOptionsLoading: boolean;
  clientSearch: string;
  onClientSearch: (value: string) => void;
  onToggleClientEditor: () => void;
  onToggleClient: (client: ZoneDriverClient) => void;
  onRemove: () => void;
}

/**
 * One roster row. The drag handle is a sibling of the remove button (never
 * nested) so grabbing to reorder can't fire a delete. The client-scope editor
 * expands inside the row: no scope = the driver serves every client.
 */
const SortableRosterRow: React.FC<SortableRosterRowProps> = ({
  driver,
  priority,
  disabled,
  clientEditorOpen,
  clientOptions,
  clientOptionsLoading,
  clientSearch,
  onClientSearch,
  onToggleClientEditor,
  onToggleClient,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: driver.id, disabled });

  // The driver's selected clients always get a checkbox — even when a search
  // narrows the fetched options past them — otherwise they could never be
  // unchecked. Selected-first so the current scope is visible at a glance.
  const rowOptions = useMemo(() => {
    const seen = new Set(driver.clients.map((c) => c.id));
    const rest = clientOptions.filter((c) => !seen.has(c.id));
    return [...driver.clients, ...rest];
  }, [clientOptions, driver.clients]);
  const selectedIds = useMemo(
    () => new Set(driver.clients.map((c) => c.id)),
    [driver.clients]
  );

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`bg-bg-white-0 ${isDragging ? 'relative z-10 shadow-card-md' : ''}`}
    >
      <div className="flex items-center gap-2 px-2 py-2">
        <button
          type="button"
          aria-label={`Reorder ${driver.name}`}
          disabled={disabled}
          className="grid size-7 shrink-0 cursor-grab touch-none place-items-center rounded-md text-icon-soft-400 hover:bg-bg-weak-50 hover:text-icon-sub-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--theme-accent-alpha-24) active:cursor-grabbing disabled:cursor-not-allowed [&_svg]:size-4"
          {...attributes}
          {...listeners}
        >
          <RiDraggable />
        </button>
        <span className="grid size-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-bold tabular-nums text-static-white">
          {priority}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] tracking-tight text-text-strong-950">
            {driver.name}
          </span>
          <span className="block truncate font-mono text-[11px] tabular-nums text-text-soft-400">
            {driver.code}
          </span>
        </span>
        <button
          type="button"
          onClick={onToggleClientEditor}
          disabled={disabled}
          aria-expanded={clientEditorOpen}
          aria-label={`Client scope for ${driver.name}`}
          title="Client scope — empty means every client"
          className={`inline-flex h-6 max-w-[132px] shrink-0 items-center gap-1 truncate rounded-full border px-2 text-[10.5px] font-semibold tracking-tight transition-colors disabled:cursor-not-allowed ${
            driver.clients.length > 0
              ? 'border-(--theme-accent-alpha-24) bg-(--theme-accent-alpha-08) text-accent'
              : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
          } ${clientEditorOpen ? 'ring-2 ring-(--theme-accent-alpha-24)' : ''}`}
        >
          <i className="ri-store-2-line text-[12px]" aria-hidden="true" />
          <span className="truncate">
            {driver.clients.length === 0
              ? 'All clients'
              : driver.clients.length === 1
                ? driver.clients[0].name
                : `${driver.clients.length} clients`}
          </span>
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${driver.name}`}
          title="Remove from roster"
          className="grid size-7 shrink-0 place-items-center rounded-md text-icon-soft-400 hover:bg-error-lighter hover:text-error-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-strong-950 disabled:cursor-not-allowed [&_svg]:size-4"
        >
          <RiDeleteBinLine />
        </button>
      </div>

      {clientEditorOpen && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50/50 px-3 py-2">
          <p className="mb-1.5 text-[10.5px] leading-relaxed text-text-sub-600">
            Limit this mitra to specific clients. Leave everything unchecked to
            serve <span className="font-semibold">every client</span>. New
            mitra start with the clients they're registered under.
          </p>
          <SearchField
            value={clientSearch}
            onChange={onClientSearch}
            placeholder="Search clients…"
            size="sm"
            disabled={disabled}
            debounceMs={0}
            className="mb-1.5"
          />
          {clientOptionsLoading && rowOptions.length === 0 ? (
            <p className="py-1 text-[11px] text-text-sub-600">
              Loading clients…
            </p>
          ) : rowOptions.length === 0 ? (
            <p className="py-1 text-[11px] text-text-sub-600">
              {clientSearch ? 'No clients match.' : 'No clients found.'}
            </p>
          ) : (
            <ul className="grid max-h-[168px] grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto">
              {rowOptions.map((c) => (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-center gap-1.5 py-0.5 text-[11.5px] tracking-tight text-text-strong-950">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      disabled={disabled}
                      onChange={() =>
                        onToggleClient({ id: c.id, name: c.name })
                      }
                      className="size-3.5 accent-(--color-accent,#5e2aac)"
                    />
                    <span className="truncate">{c.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

export default ZoneDriversDrawer;
